"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from '../../lib/utils'
import { Button } from './button'
import { Input } from './input'
import { Textarea } from './textarea'

function InputGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={cn(
        "group/input-group relative flex w-full items-center overflow-hidden rounded-lg border border-input bg-secondary/30 shadow-none outline-none transition-colors",
        "focus-within:border-primary",
        "min-h-10 has-[>textarea]:min-h-0",
        "has-[>[data-align=inline-start]]:[&>input]:pl-1",
        "has-[>[data-align=inline-end]]:[&>input]:pr-1",
        "has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3",
        "has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3",
        "has-[[data-slot][aria-invalid=true]]:border-destructive has-[[data-slot][aria-invalid=true]]:focus-within:border-destructive",
        className
      )}
      {...props}
    />
  )
}

const inputGroupAddonVariants = cva(
  "flex shrink-0 select-none items-center text-muted-foreground [&_svg]:shrink-0",
  {
    variants: {
      align: {
        "inline-start": "order-first pl-3 pr-1",
        "inline-end": "order-last pl-1 pr-3",
        "block-start":
          "order-first w-full justify-start border-b border-border px-3 py-2.5 text-body-sm font-medium text-foreground",
        "block-end":
          "order-last w-full justify-start border-t border-border px-3 pb-3 pt-2 text-caption text-muted-foreground",
      },
    },
    defaultVariants: {
      align: "inline-start",
    },
  }
)

function InputGroupAddon({
  className,
  align = "inline-start",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button")) {
          return
        }
        const control = e.currentTarget.parentElement?.querySelector('input,textarea') as
          | HTMLInputElement
          | HTMLTextAreaElement
          | null
        control?.focus()
      }}
      {...props}
    />
  )
}

const inputGroupButtonVariants = cva(
  "inline-flex shrink-0 items-center shadow-none hover:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0",
  {
    variants: {
      size: {
        xs: "h-8 gap-1 rounded-md px-2 text-body-sm has-[>svg]:px-2",
        sm: "h-8 gap-1.5 rounded-md px-2.5 text-body-sm has-[>svg]:px-2.5",
        "icon-xs": "size-8 rounded-md p-0 has-[>svg]:p-0",
        "icon-sm": "size-8 p-0 has-[>svg]:p-0",
      },
    },
    defaultVariants: {
      size: "xs",
    },
  }
)

function InputGroupButton({
  className,
  type = "button",
  variant = "ghost",
  size = "xs",
  ...props
}: Omit<React.ComponentProps<typeof Button>, "size"> &
  VariantProps<typeof inputGroupButtonVariants>) {
  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  )
}

function InputGroupText({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "text-muted-foreground flex items-center gap-2 text-body-sm [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
        className
      )}
      {...props}
    />
  )
}

const InputGroupInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<typeof Input>
>(({ className, inputSize, ...props }, ref) => (
  <Input
    ref={ref}
    data-slot="input-group-control"
    inputSize={inputSize}
    className={cn(
      "min-w-0 flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
      className
    )}
    {...props}
  />
))
InputGroupInput.displayName = "InputGroupInput"

const InputGroupTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<typeof Textarea>
>(({ className, inputSize, ...props }, ref) => (
  <Textarea
    ref={ref}
    data-slot="input-group-control"
    inputSize={inputSize}
    className={cn(
      "min-w-0 flex-1 resize-none rounded-none border-0 bg-transparent py-3 shadow-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
      className
    )}
    {...props}
  />
))
InputGroupTextarea.displayName = "InputGroupTextarea"

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupInput,
  InputGroupTextarea,
}
